// @flow
import React, { useState } from "react";
import "./styles.css";
import { GeoJSON, Map, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import Address from "../../models/address";
import Location from "../../models/location";
import townPolys from "../../data/vt_town_polygons.geo.json";
import * as R from "ramda";
import * as markerTypes from "../../constants/marker-types";

const defaultIcon = require("../../assets/pin-icons/drop-off-marker.png");
const celebrationIcon = require("../../assets/pin-icons/celebration-marker.png");
const dropOffIcon = require("../../assets/pin-icons/drop-off-marker.png");
const pickUpIcon = require("../../assets/pin-icons/pick-up-marker.png");
const teamIcon = require("../../assets/pin-icons/team-marker.png");
const trashIcon = require("../../assets/pin-icons/trash-marker.png");
const collectedTrashIcon = require("../../assets/pin-icons/trash-collected-marker.png");
const shadowUrl = require("../../assets/pin-icons/marker-shadow.png");
const MAX_LATITUDE = 85.0511287798;
const MAX_LONGITUDE = 180;

const markerIcons = {
    [markerTypes.DEFAULT]: defaultIcon,
    [markerTypes.CELEBRATION]: celebrationIcon,
    [markerTypes.DROPOFF]: dropOffIcon,
    [markerTypes.PICKUP]: pickUpIcon,
    [markerTypes.TEAM]: teamIcon,
    [markerTypes.TRASH]: trashIcon,
    [markerTypes.COLLECTED]: collectedTrashIcon
};
const areValidCoordinates = (coords = {}) => {
    const lat = parseFloat(coords.latitude);
    const lng = parseFloat(coords.longitude);
    return !isNaN(lat) &&
        !isNaN(lng) &&
        lat <= MAX_LATITUDE &&
        lat >= -1 * MAX_LATITUDE &&
        lng <= MAX_LONGITUDE &&
        lng >= -1 * MAX_LONGITUDE;
};

type PropsType = {
    labelText?: string,
    coordinates: Coordinates,
    markerType?: string,
    pinLabel?: string,
    onChange: Coordinates => void,
    showTowns?: boolean,
    zoom?: number,
    markers?: Array<{ markerType: string, latitude: number, longitude: number, pinLabel: string }>
};

export const LocationControl = ({ onChange, zoom = 8, labelText = "", coordinates = {}, markerType, markers = [], showTowns = false, pinLabel = "Location" }: PropsType) => {
    const [currentZoom, setCurrentZoom] = useState(zoom);
    const initialLocation = areValidCoordinates(coordinates) ? [coordinates.latitude, coordinates.longitude] : [44, -72.5890947];
    const icon = L.icon({
        iconUrl: markerIcons[markerType] || markerIcons.DEFAULT,
        shadowUrl,
        iconSize: [35, 40], // size of the icon
        shadowSize: [43, 31], // size of the shadow
        iconAnchor: [23, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 40], // the same for the shadow
        popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
    });

    const changeCoordinates = R.curry((key, e) => {
        const val = e.target.value;
        const newCoords = { ...coordinates, [key]: val };
        onChange(newCoords);
    });

    const handleMapClick = e => {
        setCurrentZoom(e.sourceTarget.zoom);
        onChange({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    };

    const otherMarkers = markers.map((loc: Location, index: number) => areValidCoordinates(loc.coordinates)
        ? (
            <Marker
                icon={ markerIcons[loc.marker] }
                key={ index }
                position={ loc.coordinates }
            >
                <Popup>
                    { loc.name }<br/>{ Address.toString(loc.address) }
                </Popup>
            </Marker>
        )
        : null);

    const marker = areValidCoordinates(coordinates) ? (
        <Marker
            icon={ icon }
            position={ { lat: coordinates.latitude, lng: coordinates.longitude } }
        >
            <Popup>
                { pinLabel }
            </Popup>
        </Marker>
    ) : null;
    return (
        <div className={ "location-control" }>
            <div className={ "location-control--label" }>{ labelText }</div>
            <div className={ "location-control--wrapper" }>
                <div className={ "location-control--coordinates" }>
                    {/* eslint-disable-next-line jsx-a11y/label-has-for */ }
                    <label className={ "location-control--coordinate" }>
                        <span className="location-control--coordinate-label">{ "LATITUDE" }</span>
                        <input
                            className="location-control--coordinate-input"
                            onChange={ changeCoordinates("latitude") }
                            value={ coordinates.latitude || "" }/>
                    </label>
                    {/* eslint-disable-next-line jsx-a11y/label-has-for */ }
                    <label className={ "location-control--coordinate" }>
                        <span className="location-control--coordinate-label">{ "LONGITUDE" }</span>
                        <input
                            className="location-control--coordinate-input"
                            onChange={ changeCoordinates("longitude") }
                            value={ coordinates.longitude || "" }
                        />
                    </label>
                </div>
                <Map
                    className={ "location-control--map" }
                    center={ initialLocation }
                    zoom={ currentZoom }
                    onClick={ handleMapClick }
                >
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}.png"/>
                    { showTowns
                        ? (
                            <GeoJSON
                                data={ townPolys }
                                onEachFeature={ (feature, layer) => {
                                    layer.bindTooltip(feature.properties.TOWNNAMEMC, { permanent: true });
                                } }
                            />
                        )
                        : null }
                    { marker }
                    { otherMarkers }
                </Map>
            </div>
        </div>
    );
};
